import './settingsScreen.css';
import {
  BINDING_ORDER,
  displayBindingKey,
  normalizeBindingKey,
  type BindingAction,
} from '../input/keymap.js';
import { getSettings, settingsStore } from '../settings/store.js';
import type {
  AccessibilitySettings,
  AssetQuality,
  ButtonMode,
  ColorblindPalette,
  SettingsState,
} from '../settings/types.js';

type SettingsCategory =
  | 'display'
  | 'graphics'
  | 'audio'
  | 'controls'
  | 'gameplay'
  | 'accessibility';

const CATEGORIES: readonly SettingsCategory[] = [
  'display',
  'graphics',
  'audio',
  'controls',
  'gameplay',
  'accessibility',
];

const CATEGORY_LABELS: Record<SettingsCategory, string> = {
  display: 'Display',
  graphics: 'Graphics',
  audio: 'Audio',
  controls: 'Controls',
  gameplay: 'Gameplay',
  accessibility: 'Accessibility',
};

const pct = (value: number): string => `${Math.round(value * 100)}%`;

const row = (label: string, control: string, note = ''): string => `
  <label class="ss-row">
    <span class="ss-copy">
      <span class="ss-label">${label}</span>
      ${note ? `<span class="ss-note">${note}</span>` : ''}
    </span>
    <span class="ss-control">${control}</span>
  </label>`;

const toggle = (name: string, value: boolean, text: string): string =>
  `<button class="ss-toggle${value ? ' on' : ''}" data-action="toggle" data-name="${name}">${text}</button>`;

const slider = (name: string, value: number, min: number, max: number, step: number): string =>
  `<input class="ss-range" type="range" min="${min}" max="${max}" step="${step}" value="${value}" data-action="range" data-name="${name}">
   <output class="ss-output" data-output="${name}">${sliderText(name, value)}</output>`;

const select = (name: string, value: string, options: readonly string[]): string =>
  `<select class="ss-select" data-action="select" data-name="${name}">
    ${options.map((option) => optionMarkup(value, option)).join('')}
  </select>`;

const optionMarkup = (current: string, option: string): string => {
  const selected = current === option ? ' selected' : '';
  return `<option value="${option}"${selected}>${optionLabel(option)}</option>`;
};

const optionLabel = (value: string): string =>
  OPTION_LABELS[value] ?? value.replaceAll('-', ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const OPTION_LABELS: Record<string, string> = {
  '0': 'Unlimited',
  '1.6': 'Short',
  '2.4': 'Balanced',
  '3.2': 'Extended',
  decimated: 'Decimated',
  hq: 'High Quality',
};

const sliderText = (name: string, value: number): string => {
  if (name.includes('frameCap')) return value === 0 ? 'Unlimited' : `${value} FPS`;
  if (name.includes('renderScale')) return `${value.toFixed(2)}x`;
  if (name.includes('verifyBeatSec')) return `${value.toFixed(1)}s`;
  return pct(value);
};

const keybindButton = (action: BindingAction, pending: BindingAction | null, key: string): string => {
  const label = pending === action ? 'PRESS KEY' : displayBindingKey(key);
  return `<button class="ss-bind${pending === action ? ' waiting' : ''}" data-action="rebind" data-bind="${action}">${label}</button>`;
};

const groupedBindings = (pending: BindingAction | null, keymap: SettingsState['controls']['keymap']): string =>
  BINDING_ORDER.map((binding) =>
    row(
      binding.label,
      keybindButton(binding.action, pending, keymap[binding.action]),
      binding.category,
    ),
  ).join('');

export class SettingsScreen {
  private readonly root: HTMLDivElement;
  private readonly tabs: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private open = false;
  private category: SettingsCategory = 'display';
  private pendingRebind: BindingAction | null = null;
  private onClose: (() => void) | null = null;

  constructor() {
    this.root = document.createElement('div');
    this.root.id = 'settings-screen';
    this.root.innerHTML = `
      <div class="ss-card">
        <div class="ss-head">
          <div>
            <div class="ss-folio">FOLIO SETTINGS</div>
            <div class="ss-title">The Ledger Register</div>
          </div>
          <button class="ss-close" data-action="close">Return</button>
        </div>
        <div class="ss-body">
          <div class="ss-tabs"></div>
          <div class="ss-panel"></div>
        </div>
      </div>`;
    this.root.style.display = 'none';
    document.body.appendChild(this.root);
    this.tabs = this.root.querySelector('.ss-tabs') as HTMLDivElement;
    this.panel = this.root.querySelector('.ss-panel') as HTMLDivElement;
    this.root.addEventListener('click', (event) => this.onClick(event));
    this.root.addEventListener('input', (event) => this.onInput(event));
    this.root.addEventListener('change', (event) => this.onChange(event));
    window.addEventListener('keydown', (event) => this.onKey(event));
    document.addEventListener('fullscreenchange', () => this.syncFullscreenSetting());
  }

  get isOpen(): boolean {
    return this.open;
  }

  show(category: SettingsCategory, onClose?: () => void): void {
    this.category = category;
    this.open = true;
    this.pendingRebind = null;
    this.onClose = onClose ?? null;
    this.render();
    this.root.style.display = 'flex';
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.pendingRebind = null;
    this.root.style.display = 'none';
    const close = this.onClose;
    this.onClose = null;
    close?.();
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }

  private render(): void {
    this.tabs.innerHTML = this.renderTabs();
    this.panel.innerHTML = this.renderPanel();
  }

  private renderTabs(): string {
    return CATEGORIES.map((category) => {
      const current = category === this.category ? ' current' : '';
      return `<button class="ss-tab${current}" data-action="tab" data-category="${category}">${CATEGORY_LABELS[category]}</button>`;
    }).join('');
  }

  private renderPanel(): string {
    if (this.category === 'display') return this.renderDisplay();
    if (this.category === 'graphics') return this.renderGraphics();
    if (this.category === 'audio') return this.renderAudio();
    if (this.category === 'controls') return this.renderControls();
    if (this.category === 'gameplay') return this.renderGameplay();
    return this.renderAccessibility();
  }

  private renderDisplay(): string {
    const { display } = getSettings();
    return `
      <div class="ss-section">${row('Browser Fullscreen', toggle('display.fullscreenPreferred', display.fullscreenPreferred, display.fullscreenPreferred ? 'On' : 'Off'), 'Applies on the next user gesture when supported.')}</div>
      <div class="ss-section">${row('Frame Cap', select('display.frameCap', String(display.frameCap), ['0', '30', '60', '120']))}</div>
      <div class="ss-section">${row('Render Scale', slider('display.renderScale', display.renderScale, 0.75, 1.25, 0.05), 'Internal render resolution.')}</div>
      <div class="ss-section">${row('Vignette Intensity', slider('display.vignetteIntensity', display.vignetteIntensity, 0, 0.8, 0.02))}</div>`;
  }

  private renderGraphics(): string {
    const { graphics } = getSettings();
    return `
      <div class="ss-section">${row('Film Grain', toggle('graphics.filmGrainEnabled', graphics.filmGrainEnabled, graphics.filmGrainEnabled ? 'On' : 'Off'))}</div>
      <div class="ss-section">${row('Film Grain Intensity', slider('graphics.filmGrainIntensity', graphics.filmGrainIntensity, 0, 0.08, 0.005))}</div>
      <div class="ss-section">${row('Bloom', toggle('graphics.bloomEnabled', graphics.bloomEnabled, graphics.bloomEnabled ? 'On' : 'Off'))}</div>
      <div class="ss-section">${row('Scanline', toggle('graphics.scanlineEnabled', graphics.scanlineEnabled, graphics.scanlineEnabled ? 'On' : 'Off'), 'Instrument layer only.')}</div>
      <div class="ss-section">${row('Scanline Intensity', slider('graphics.scanlineIntensity', graphics.scanlineIntensity, 0, 1, 0.05))}</div>
      <div class="ss-section">${row('Asset Quality', select('graphics.assetQuality', graphics.assetQuality, ['decimated', 'hq']), 'Changing this reloads the client.')}</div>`;
  }

  private renderAudio(): string {
    const { audio } = getSettings();
    return `
      <div class="ss-section">${row('Mute', toggle('audio.muted', audio.muted, audio.muted ? 'Muted' : 'Live'))}</div>
      <div class="ss-section">${row('Master', slider('audio.master', audio.master, 0, 1, 0.01))}</div>
      <div class="ss-section">${row('Music', slider('audio.music', audio.music, 0, 1, 0.01), 'Phase 3 bus already wired.')}</div>
      <div class="ss-section">${row('SFX', slider('audio.sfx', audio.sfx, 0, 1, 0.01))}</div>
      <div class="ss-section">${row('UI', slider('audio.ui', audio.ui, 0, 1, 0.01))}</div>
      <div class="ss-section">${row('Ambience', slider('audio.ambience', audio.ambience, 0, 1, 0.01))}</div>
      <div class="ss-section">${row('Voice', slider('audio.dialogue', audio.dialogue, 0, 1, 0.01), 'Campaign comms voice-over.')}</div>
      <div class="ss-section">${row('Radio Filter', toggle('audio.radioFilter', audio.radioFilter, audio.radioFilter ? 'On' : 'Off'), 'Band-pass + compression so comms read as diegetic radio.')}</div>`;
  }

  private renderControls(): string {
    const { controls } = getSettings();
    return `
      <div class="ss-section">${row('Assist Key Mode', select('controls.assistMode', controls.assistMode, ['toggle', 'hold']), 'Hold mode flips on key down and restores on release.')}</div>
      <div class="ss-section">${row('Boost Key Mode', select('controls.boostMode', controls.boostMode, ['hold', 'toggle']), 'Hold mode re-fires as soon as cooldown allows.')}</div>
      <div class="ss-section ss-copy-only">Gamepad note: keyboard remap is live now; gamepad routing remains a later wave.</div>
      <div class="ss-section ss-bindings">${groupedBindings(this.pendingRebind, controls.keymap)}</div>`;
  }

  private renderGameplay(): string {
    const { gameplay } = getSettings();
    return `
      <div class="ss-section">${row('Flight Assists Default', toggle('gameplay.flightAssistDefault', gameplay.flightAssistDefault, gameplay.flightAssistDefault ? 'On' : 'Off'))}</div>
      <div class="ss-section">${row('Hints', toggle('gameplay.hintsEnabled', gameplay.hintsEnabled, gameplay.hintsEnabled ? 'On' : 'Off'))}</div>
      <div class="ss-section">${row('Verification Timing', select('gameplay.verifyBeatSec', String(gameplay.verifyBeatSec), ['1.6', '2.4', '3.2']))}</div>
      <div class="ss-section">${row('No-Fail Reading Mode', toggle('gameplay.noFailReadingMode', gameplay.noFailReadingMode, gameplay.noFailReadingMode ? 'On' : 'Off'), 'Incoming hostile damage becomes non-fatal.')}</div>`;
  }

  private renderAccessibility(): string {
    const { accessibility } = getSettings();
    return `
      <div class="ss-section">${row('Colorblind Palette', select('accessibility.colorblindPalette', accessibility.colorblindPalette, ['off', 'deuteranopia', 'protanopia', 'tritanopia']), 'Moral-grammar hues remapped at the root.')}</div>
      <div class="ss-section">${row('Palette Intensity', slider('accessibility.colorblindIntensity', accessibility.colorblindIntensity, 0, 1, 0.05))}</div>
      <div class="ss-section ss-copy-only">Truth / contradiction now carry glyph and audio stamps: ✓ truth, ✕ contradiction.</div>
      <div class="ss-section">${row('Dyslexia-Friendly Font', toggle('accessibility.dyslexiaFont', accessibility.dyslexiaFont, accessibility.dyslexiaFont ? 'On' : 'Off'), 'Cinzel carved names stay untouched.')}</div>
      <div class="ss-section">${row('Reduce Motion', toggle('accessibility.reduceMotion', accessibility.reduceMotion, accessibility.reduceMotion ? 'On' : 'Off'))}</div>
      <div class="ss-section">${row('Scanline / Grain / Vignette Master', toggle('accessibility.disableScreenFx', accessibility.disableScreenFx, accessibility.disableScreenFx ? 'Off' : 'On'))}</div>
      <div class="ss-section">${row('Text Size', slider('accessibility.textScale', accessibility.textScale, 0.85, 1.35, 0.05))}</div>
      <div class="ss-section">${row('Record Card Opacity', slider('accessibility.cardOpacity', accessibility.cardOpacity, 0.7, 1, 0.02))}</div>
      <div class="ss-section">${row('Subtitle / Comms Size', slider('accessibility.commsScale', accessibility.commsScale, 0.85, 1.4, 0.05))}</div>
      <div class="ss-section">${row('Comms Auto-Advance', toggle('accessibility.voiceAutoAdvance', accessibility.voiceAutoAdvance, accessibility.voiceAutoAdvance ? 'On' : 'Off'), 'Advance comms when the voice clip ends — no input needed.')}</div>`;
  }

  private onClick(event: MouseEvent): void {
    if (!this.open) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!target) return;
    const action = target.dataset.action;
    if (action === 'close') this.hide();
    else if (action === 'tab') this.switchCategory(target.dataset.category as SettingsCategory);
    else if (action === 'toggle') this.toggleSetting(target.dataset.name as string);
    else if (action === 'rebind') this.beginRebind(target.dataset.bind as BindingAction);
  }

  private onInput(event: Event): void {
    if (!this.open) return;
    const input = event.target as HTMLInputElement;
    if (input.dataset.action !== 'range') return;
    this.updateValue(input.dataset.name as string, Number(input.value));
    this.syncOutput(input.dataset.name as string, Number(input.value));
  }

  private onChange(event: Event): void {
    if (!this.open) return;
    const target = event.target as HTMLSelectElement;
    if (target.dataset.action !== 'select') return;
    this.updateSelect(target.dataset.name as string, target.value);
    this.render();
  }

  private onKey(event: KeyboardEvent): void {
    if (!this.open) return;
    if (this.pendingRebind) {
      event.preventDefault();
      if (event.key === 'Escape') this.cancelRebind();
      else this.finishRebind(normalizeBindingKey(event.key));
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.hide();
    }
  }

  private switchCategory(category: SettingsCategory): void {
    this.category = category;
    this.pendingRebind = null;
    this.render();
  }

  private beginRebind(action: BindingAction): void {
    this.pendingRebind = action;
    this.render();
  }

  private cancelRebind(): void {
    this.pendingRebind = null;
    this.render();
  }

  private finishRebind(key: string): void {
    const action = this.pendingRebind;
    if (!action) return;
    settingsStore.update((settings) => ({
      ...settings,
      controls: {
        ...settings.controls,
        keymap: { ...settings.controls.keymap, [action]: key },
      },
    }));
    this.pendingRebind = null;
    this.render();
  }

  private toggleSetting(name: string): void {
    if (name === 'display.fullscreenPreferred') this.toggleFullscreen();
    else this.updateValue(name, !this.readBoolean(name));
    this.render();
  }

  private toggleFullscreen(): void {
    const next = !getSettings().display.fullscreenPreferred;
    this.updateValue('display.fullscreenPreferred', next);
    if (next && !document.fullscreenElement) void document.documentElement.requestFullscreen?.();
    if (!next && document.fullscreenElement) void document.exitFullscreen();
  }

  private syncFullscreenSetting(): void {
    const active = Boolean(document.fullscreenElement);
    if (active === getSettings().display.fullscreenPreferred) return;
    this.updateValue('display.fullscreenPreferred', active);
    if (this.open && this.category === 'display') this.render();
  }

  private readBoolean(name: string): boolean {
    const [section, field] = name.split('.') as [keyof SettingsState, string];
    const sectionState = getSettings()[section] as unknown as Record<string, unknown>;
    return Boolean(sectionState[field]);
  }

  private updateSelect(name: string, value: string): void {
    if (name === 'graphics.assetQuality') this.updateAssetQuality(value as AssetQuality);
    else if (name === 'controls.assistMode') this.updateControlMode('assistMode', value as ButtonMode);
    else if (name === 'controls.boostMode') this.updateControlMode('boostMode', value as ButtonMode);
    else if (name === 'accessibility.colorblindPalette') this.updatePalette(value as ColorblindPalette);
    else this.updateValue(name, Number.isNaN(Number(value)) ? value : Number(value));
  }

  private updateAssetQuality(value: AssetQuality): void {
    if (value === getSettings().graphics.assetQuality) return;
    settingsStore.update((settings) => ({
      ...settings,
      graphics: { ...settings.graphics, assetQuality: value },
    }));
    window.setTimeout(() => location.reload(), 120);
  }

  private updateControlMode(name: 'assistMode' | 'boostMode', value: ButtonMode): void {
    settingsStore.update((settings) => ({
      ...settings,
      controls: { ...settings.controls, [name]: value },
    }));
  }

  private updatePalette(value: AccessibilitySettings['colorblindPalette']): void {
    settingsStore.update((settings) => ({
      ...settings,
      accessibility: { ...settings.accessibility, colorblindPalette: value },
    }));
  }

  private updateValue(name: string, value: boolean | number | string): void {
    const [section, field] = name.split('.') as [keyof SettingsState, string];
    settingsStore.update((settings) => ({
      ...settings,
      [section]: {
        ...settings[section],
        [field]: value,
      },
    }));
  }

  private syncOutput(name: string, value: number): void {
    const output = this.panel.querySelector<HTMLOutputElement>(`[data-output="${name}"]`);
    if (output) output.textContent = sliderText(name, value);
  }
}
